import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Trash2, RefreshCw, Terminal, X } from "lucide-react";
import { api, getApiError } from "../../api/client";
import { useNavigate, useParams } from "react-router-dom";
import StatCard from "./Cards/StatCard";
import SyntaxCodeViewer from "./SyntaxCodeViewer";
import RegenerateScriptModal from "./RegenerateScriptModal";

const VIEWABLE_STATUSES = new Set([
  "ready_to_execute",
  "regenerating_script",
  "executing",
  "succeeded",
  "failed",
]);


function downloadFile(content, filename) {
  const blob = new Blob([content], { type: "text/plain" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


export default function ScriptViewer({ run, onRefresh }) {
  const navigate              = useNavigate();
  const { runId }             = useParams();

  const [script, setScript]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [showCleanup, setShowCleanup] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showGuide, setShowGuide]     = useState(false);

  const fetchScript = useCallback(async () => {
    if (!VIEWABLE_STATUSES.has(run.status)) return;
    try {
      const { data } = await api.get(`/golive/scripts/${run.id}`);
      setError("");
      setScript(data);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setLoading(false);
    }
  }, [run.id, run.status]);

  useEffect(() => {
    fetchScript();
  }, [fetchScript]);

  if (run.status === "generating_script") {
    return (
      <div className="gl-card">
        <div className="flex flex-col items-center gap-3 py-10">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Generating deployment script…</p>
          <p className="text-xs text-slate-400">This usually takes 20–40 seconds.</p>
        </div>
      </div>
    );
  }

  if (!VIEWABLE_STATUSES.has(run.status)) {
    return (
      <div className="gl-card">
        <div className="gl-empty">
          Script will appear here once the proposal is approved and generation completes.
        </div>
      </div>
    );
  }

  if (loading) return <div className="gl-card"><div className="gl-empty">Loading script…</div></div>;
  if (error)   return <div className="gl-card"><div className="gl-error">{error}</div></div>;
  if (!script) return null;

  const isTerraform = script.format === "terraform";
  const deployFile  = isTerraform ? "deploy.sh"   : "playbook.yml";
  const cleanupFile = isTerraform ? "destroy.sh"  : "cleanup.yml";
  const langKey     = isTerraform ? "bash"         : "yaml";

  const EXECUTION_GUIDE = isTerraform ? {
    note: "Save deploy.sh at the root of your repository — the same folder that contains your backend source and frontend directories. The script is idempotent: re-running it skips resources that already exist and only applies what changed. Do NOT commit deploy.sh after filling in real values — it contains secrets.",
    configChecklist: [
      { key: "PROJECT_ID", example: "my-project-123", desc: "Your GCP project ID. Find it on the GCP Console home page under Project info, or in the project selector dropdown." },
      { key: "REGION",     example: "us-central1",    desc: "The GCP region where all resources will be deployed. Use the same region as any existing resources to avoid cross-region costs." },
      { key: "Secrets",    example: "SECRET_KEY, REDIS_URL, …", desc: "Each secret variable is named after the env var it maps to. Paste real values before running. DATABASE_URL is auto-generated from the provisioned Cloud SQL instance — do not fill it in." },
      { key: "GOOGLE_APPLICATION_CREDENTIALS (optional)", example: "/path/to/key.json", desc: "Only needed if your organisation blocks browser-based gcloud auth. Create a service account with Owner role, download its JSON key, and set this to the file path. Also add: gcloud auth activate-service-account --key-file=\"$GOOGLE_APPLICATION_CREDENTIALS\" on the next line." },
    ],
    prerequisites: [
      {
        label: "Terraform CLI",
        detail: "Used internally by the script to provision GCP infrastructure (Cloud SQL, Secret Manager, IAM, etc.).",
        link: "https://developer.hashicorp.com/terraform/install",
      },
      {
        label: "gcloud CLI",
        detail: "Google Cloud's command-line tool — used for authentication, Secret Manager, and Cloud Run deployment. After installing, open a new terminal so the PATH is updated.",
        link: "https://cloud.google.com/sdk/docs/install",
      },
      {
        label: "A GCP project with billing enabled",
        detail: "All resources are provisioned inside this project. Make sure billing is active — Cloud SQL and Cloud Run require it.",
        link: null,
      },
      {
        label: "Node.js + npm (only if Firebase Hosting is included)",
        detail: "Required to build the frontend before deploying. The script uses npx firebase-tools so no separate firebase install is needed.",
        link: "https://nodejs.org",
      },
      {
        label: "firebase.json in your frontend directory (only if Firebase Hosting is included)",
        detail: "Must exist and must not use a named target field. The public path should point to your build output directory (e.g. dist for Vite, build for CRA). Rewrites for Cloud Run must reference the correct serviceId and region.",
        link: null,
      },
    ],
    steps: [
      {
        cmd: null,
        desc: "Download deploy.sh and save it to the root of your repository — the same folder that contains your backend and frontend directories. Add deploy.sh, destroy.sh, and .golive-tf-*/ to .gitignore before filling in any values.",
      },
      {
        cmd: "gcloud auth application-default login",
        desc: "Authenticate with Google Cloud. A browser window will open — sign in with an account that has Owner or Editor access. If your organisation blocks this (corporate Google account), use a service account key instead — see GOOGLE_APPLICATION_CREDENTIALS in the config section above.",
      },
      {
        cmd: null,
        desc: "Open deploy.sh and fill in PROJECT_ID, REGION, and every secret variable marked REPLACE_ME. DATABASE_URL is auto-generated — leave it blank. Make sure secret values (e.g. ENVIRONMENT) match what your app expects — an unexpected value can cause a startup crash.",
      },
      {
        cmd: "bash deploy.sh",
        desc: "Run from your repository root. The script enables APIs, provisions infrastructure via Terraform, uploads secrets, deploys to Cloud Run, and (if applicable) builds and deploys the frontend to Firebase Hosting. The live URLs are printed at the end.",
      },
    ],
  } : {
    note: "You do not need to add this file to your repository. Just download playbook.yml to any folder on your machine and run the commands below from that folder.",
    configChecklist: [
      { key: "app_dir",       example: "/var/www/myapp",  desc: "The directory on the target server where the application will be deployed." },
      { key: "ansible_user",  example: "ubuntu",          desc: "The SSH user Ansible will connect as (e.g. ubuntu, ec2-user, root)." },
      { key: "ansible_host",  example: "192.168.1.10",    desc: "The IP address or hostname of your target server." },
    ],
    prerequisites: [
      {
        label: "Ansible",
        detail: "The tool that executes the playbook on your target server.",
        link: "https://docs.ansible.com/ansible/latest/installation_guide/index.html",
      },
      {
        label: "SSH access to the target host",
        detail: "Ansible connects to your server over SSH. Make sure you can SSH into it from your machine.",
        link: null,
      },
      {
        label: "Python 3 on the target host",
        detail: "Ansible requires Python 3 to be installed on the remote server (not your local machine).",
        link: null,
      },
    ],
    steps: [
      {
        cmd: null,
        desc: "Download playbook.yml and save it to any folder on your machine (e.g. my-deployment/).",
      },
      {
        cmd: "cd my-deployment",
        desc: "Open a terminal and navigate to the folder where you saved playbook.yml.",
      },
      {
        cmd: "ansible --version",
        desc: "Confirm Ansible is installed. If this fails, follow the install link in the prerequisites above.",
      },
      {
        cmd: "ansible all -i <your-host>, -m ping",
        desc: "Test that Ansible can reach your target server over SSH. Replace <your-host> with the IP address or hostname of your server. The trailing comma is required when passing a single host inline.",
      },
      {
        cmd: "ansible-playbook playbook.yml",
        desc: "Run the deployment from the target VM itself. The playbook uses connection: local so no inventory file is needed — Ansible applies every task directly on this machine.",
      },
    ],
  };


  return (
    <div className="flex flex-col gap-5">

      {/* ── Meta cards: TARGET · FORMAT ── */}
      <div className="flex gap-4 flex-wrap">
        {[
          { label: "Target", value: run.target },
          // { label: "Approved by", value: run.approver_name ?? "—" },
          { label: "Format", value: `${script.format} · ${script.deployment_lines} lines` },
        ].map(({ label, value }) => (
          <StatCard key={label} label={label} count={value} />
        ))}
      </div>

      {/* ── Deployment script ── */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deployment script</p>
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-200 bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors"
          >
            <Terminal size={13} />
            How to run this
          </button>
        </div>
        <SyntaxCodeViewer
          code={script.deployment_script}
          filename={deployFile}
          language={langKey}
          badge={`${script.format} · attempt ${script.attempt_number}`}
          onDownload={() => downloadFile(script.deployment_script, deployFile)}
        />
      </div>

      {/* ── Execution guide modal ── */}
      {showGuide && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-36"
          onClick={() => setShowGuide(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ width: 620, maxWidth: "95vw", maxHeight: "75vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — never scrolls */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                  <Terminal size={15} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">How to run this script</p>
                  <p className="text-xs text-slate-500 mt-0.5">Follow these steps on the machine where you plan to deploy.</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowGuide(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex flex-col gap-5 px-6 py-5 overflow-y-auto flex-1">

            {/* No-repo note */}
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
              <span className="text-blue-500 text-base leading-none shrink-0 mt-0.5">ℹ</span>
              <p className="text-xs text-blue-700 leading-relaxed">{EXECUTION_GUIDE.note}</p>
            </div>

            {/* Configure before running */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Configure before running</p>
              <p className="text-xs text-slate-500 mb-3">
                Open the downloaded script in a text editor and update these values before running any commands.
              </p>
              <div className="flex flex-col gap-2">
                {EXECUTION_GUIDE.configChecklist.map(({ key, example, desc }) => (
                  <div key={key} className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
                    <span className="text-amber-500 shrink-0 mt-0.5">✎</span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xs font-mono font-semibold text-amber-700">{key}</code>
                        <span className="text-xs text-slate-400">e.g.</span>
                        <code className="text-xs font-mono text-slate-500">{example}</code>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Prerequisites */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Prerequisites</p>
              <div className="flex flex-col gap-2">
                {EXECUTION_GUIDE.prerequisites.map(({ label, detail, link }) => (
                  <div key={label} className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 shrink-0 mt-1.5" />
                    <div>
                      {link
                        ? <a href={link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:underline">{label} ↗</a>
                        : <span className="text-xs font-semibold text-slate-700">{label}</span>
                      }
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Steps */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Steps</p>
              <ol className="flex flex-col">
                {EXECUTION_GUIDE.steps.map(({ cmd, desc }, i) => {
                  const isLast = i === EXECUTION_GUIDE.steps.length - 1;
                  return (
                    <li key={i} className="flex gap-4">
                      {/* Step indicator + connector line */}
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {i + 1}
                        </div>
                        {!isLast && <div className="w-px flex-1 bg-green-100 my-1" />}
                      </div>

                      {/* Content */}
                      <div className={`flex flex-col gap-1.5 flex-1 min-w-0 ${isLast ? "pb-0" : "pb-5"}`}>
                        {cmd ? (
                          <code className="block w-full text-xs font-mono bg-slate-900 text-green-400 rounded-lg px-4 py-2.5">
                            {cmd}
                          </code>
                        ) : (
                          <p className="text-sm font-medium text-slate-700">{desc}</p>
                        )}
                        {cmd && <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-100 shrink-0">
              <button
                type="button"
                onClick={() => setShowGuide(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-slate-600 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Clean-up script section ── */}
      <div className="flex flex-col gap-3">
        {/* Cleanup header card — click to toggle */}
        <button
          type="button"
          onClick={() => setShowCleanup((v) => !v)}
          className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 w-full text-left hover:bg-gray-50 transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <Trash2 size={16} className="text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">Clean-up script</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Undoes every change made by the deployment script — destroys provisioned
              resources, removes secrets, and tears the environment back to its prior state.
            </p>
          </div>
          <span className="text-xs font-mono text-slate-400 shrink-0 whitespace-nowrap">
            {showCleanup ? "Hide ▲" : "Show ▼"}
          </span>
        </button>

        {showCleanup && (
          <>
            {/* Danger warning */}
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <span className="text-red-500 shrink-0 mt-0.5 text-base leading-none">⚠</span>
              <p className="text-sm text-slate-700 leading-relaxed">
                Running this will{" "}
                <strong className="text-red-600">permanently destroy</strong>{" "}
                all resources provisioned by{" "}
                <code className="text-xs bg-red-100 px-1 py-0.5 rounded font-mono">{deployFile}</code>.{" "}
                Use only when you intend to roll the environment back.
              </p>
            </div>

            {/* Cleanup code block */}
            <SyntaxCodeViewer
              code={script.cleanup_script}
              filename={cleanupFile}
              language={langKey}
              badge={`${script.format} · ${script.cleanup_lines} lines`}
              onDownload={() => downloadFile(script.cleanup_script, cleanupFile)}
            />
          </>
        )}
      </div>

      {/* ── Bottom action bar ── */}
      <div className="flex items-center justify-between gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4">
        <p className="text-xs text-slate-500 leading-relaxed" style={{ maxWidth: 520 }}>
          Download and run this script manually against your{" "}
          <strong className="text-slate-700">{run.target}</strong> environment.{" "}
          The cleanup script reverses all changes.
        </p>
        {run.status !== "regenerating_script" && (
          <button
            className="gl-btn"
            onClick={() => setShowFeedback(true)}
          >
            <RefreshCw size={14} /> Re-run analysis
          </button>
        )}
      </div>

      {showFeedback && (
        <RegenerateScriptModal
          runId={run.id}
          onClose={() => setShowFeedback(false)}
          onSuccess={() => { setShowFeedback(false); onRefresh(); }}
        />
      )}
    </div>
  );
}
